import { useQuery } from '@apollo/client';
import { ButtonDock, miniButtonDockScrollBottomPad, setTemplateTranslations, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { List, Placeholder, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { Page } from 'components/Page';
import dayjs from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables, GetTemplatesQuery, QuestStatus } from '../client/generated/graphql';
import { getQuestsByCategoryQuery } from '../client/queries/quest';
import { getTemplatesQuery } from '../client/queries/template';
import type { ListCategory, ListGoal } from '../client/types';
import { CategoryQuestListSkeleton } from '../components/category/skeleton/CategoryQuestListSkeleton';
import { CategorySearchEditRow } from '../components/category/CategorySearchEditRow';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { FocusLeePlaceholderImage } from '../components/common/FocusLeePlaceholderImage';
import { QuestItem } from '../components/quest/QuestItem';
import { RitualCard } from '../components/ritual/RitualCard';
import { getColor } from '../helpers/color';
import { getCategoryExternalTemplate } from '../helpers/externalCategory';
import { openExternalLink } from '../helpers/externalLink';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { categoryEditPath, questCreatePath, questViewPath, ritualEditPath, ritualViewPath } from '../navigation/paths';

export const CategoryQuestListPage: FC = () => {
  const { t } = useTranslation();
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState(false);

  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const category = useMemo(() => categories.find(c => c.id === categoryId), [categories, categoryId]);
  const { data: templatesData } = useQuery<GetTemplatesQuery>(getTemplatesQuery, { fetchPolicy: 'cache-first' });
  if (templatesData) setTemplateTranslations(templatesData.templateCategories);
  const externalTemplate = useMemo(
    () => getCategoryExternalTemplate(category, templatesData?.templateCategories),
    [category, templatesData],
  );

  const { loading, error, data } = useQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>(
    getQuestsByCategoryQuery,
    {
      variables: { categoryId: categoryId! },
      skip: !categoryId,
      fetchPolicy: 'cache-first'
    }
  );

  const quests = data?.questsByCategory ?? [];
  const goals = data?.goals ?? [];
  const categoryName = category ? translateTemplateText(t, category.templateId, 'name', category.name) : '';
  const externalTemplateName = externalTemplate ? translateTemplateText(t, externalTemplate.id, 'name', externalTemplate.name) : '';

  const filteredQuests = useMemo(() => quests.filter(q => q.status !== QuestStatus.Done), [quests]);

  // Sort quests: dueDate first (past to future), then by name
  const sortedQuests = useMemo(() => {
    return [...filteredQuests].sort((a, b) => {
      const hasA = !!a.dueDate;
      const hasB = !!b.dueDate;
      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;
      if (hasA && hasB) {
        const dateA = dayjs(a.dueDate!);
        const dateB = dayjs(b.dueDate!);
        return dateA.valueOf() - dateB.valueOf();
      }
      return translateTemplateText(t, a.templateId, 'name', a.name).localeCompare(translateTemplateText(t, b.templateId, 'name', b.name));
    });
  }, [filteredQuests, t]);

  // Filter sorted quests by name
  const filteredSortedQuests = useMemo(() => 
    sortedQuests.filter(q => translateTemplateText(t, q.templateId, 'name', q.name).toLowerCase().includes(searchTerm.toLowerCase())),
    [sortedQuests, searchTerm, t]
  );

  // Build maps for QuestItem
  const categoryMap = useMemo<Record<string, ListCategory>>(() => 
    Object.fromEntries(categories.map(c => [c.id, c])),
    [categories]
  );
  
  const goalMap = useMemo<Record<string, ListGoal>>(() => {
    if (!goals) return {};
    return Object.fromEntries(goals.map((g: ListGoal) => [g.id, g]));
  }, [goals]);

  const handleNewQuest = useCallback(() => {
    if (!categoryId) return;
    const path = questCreatePath.replace(':categoryId?', categoryId);
    navigate(path);
  }, [navigate, categoryId]);

  const handleBack = useCallback(() => navigate(-1), []);
  const handleOpenExternal = useCallback(() => openExternalLink(externalTemplate?.externalLink), [externalTemplate]);

  const showButtons = !!category && (!category.system || !!externalTemplate);

  const isLoading = loading || categoriesLoading;
  if (isLoading) return <CategoryQuestListSkeleton />;
  if (error) return <CategoryQuestListSkeleton />;

  const handleEditCategory = () => {
    if (!categoryId) return;
    hapticFeedback.impactOccurred.ifAvailable('light');
    const path = categoryEditPath.replace(':categoryId', categoryId);
    navigate(path);
  };

  return (
    <>
      <Page
        back
        insetEnabled={false}
        footer={
          <Stack>
            <CategorySearchEditRow
              searchOpen={searchOpen}
              searchQuery={searchTerm}
              onSearchQueryChange={setSearchTerm}
              onOpenSearch={() => setSearchOpen(true)}
              onCloseSearch={() => {
                setSearchOpen(false);
                setSearchTerm('');
              }}
              showEditButton={!category?.system}
              onEditCategory={handleEditCategory}
            />
            <ButtonDock
              secondary={{ text: t('actions.back'), onClick: handleBack, navigation: 'back' }}
              main={{
                text: externalTemplate ? t('category.open', { name: externalTemplateName }) : t('quest.new'),
                onClick: externalTemplate ? handleOpenExternal : handleNewQuest,
                enabled: showButtons,
                bgColor: getButtonThemeColors(showButtons).backgroundColor,
              }}
            />
          </Stack>
        }
      >
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1} mt={1} mb={3}>
          {category && (
            <Box sx={{
              width: 30,
              height: 30,
              borderRadius: 2,
              background: getColor(category.iconColor),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CategoryIcon icon={category.icon} iconColor={category.iconColor} customIcon={category.customIcon} type="filled" color="white" size={20} />
            </Box>
          )}
          <Text weight="2" style={{ fontSize: '18px' }}>{categoryName || t('category.title')}</Text>
        </Stack>
        {filteredSortedQuests.length === 0 ? (
          <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
            <Placeholder
                header={searchTerm ? t('category.noSearchResults') : t('category.noQuests')}
                description={searchTerm ? t('category.adjustSearch') : t('quest.empty.description')}
                style={{ padding: 0 }}
            >
                <FocusLeePlaceholderImage kind={searchTerm ? 'categorySearch' : 'quests'} />
            </Placeholder>
        </Stack>
        ) : (
           <List style={{ flex: 1, overflowY: 'auto', padding: 10, paddingBottom: `calc(${miniButtonDockScrollBottomPad} + 60px)` }}>
             <Stack gap={1}>
               {filteredSortedQuests.map(q => {
                 const path = (q.ritualType ? ritualViewPath : questViewPath).replace(':id', q.id);
                 return q.ritualType ? (
                   <RitualCard
                     key={q.id}
                     quest={q}
                     onClick={() => navigate(ritualEditPath.replace(':ritualType', q.ritualType!))}
                     onActionClick={() => navigate(path)}
                   />
                 ) : (
                   <QuestItem
                     key={q.id}
                     quest={q}
                     overdue={false}
                     dueDateVerbose={true}
                     categoryMap={categoryMap}
                     goalMap={goalMap}
                     onClick={() => navigate(path)}
                   />
                 );
               })}
             </Stack>
           </List>
        )}
      </Page>
    </>
  );
};
